import React, { useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { useProductDependencies } from '../../../../../controllers/use-product-dependencies';
import {
	DEFAULT_UNSPLASH_QUERY_WORD,
	PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD,
} from '../../constants';
import type { UnsplashResult } from '../../types';
import { getUnsplashPhotos } from '../unsplash-api';
import { isUnsplashImage } from '../unsplash-image';

import { messages } from './messages';

const isBadStatusError = (error: Error): boolean => error.message.includes('400');
const isRateLimitedError = (error: Error): boolean => error.message.includes('429');

export enum UnsplashFetchError {
	NO_NETWORK_ERROR = 'network_error',
	RATE_LIMITED_ERROR = 'rate_limited_error',
	BAD_STATUS_ERROR = 'bad_status_error',
}

const i18nErrorText = {
	[UnsplashFetchError.NO_NETWORK_ERROR]: (
		<FormattedMessage {...messages.unsplashApiNoNetworkErrorMessage} />
	),
	[UnsplashFetchError.BAD_STATUS_ERROR]: (
		<FormattedMessage {...messages.unsplashApiBadStatusErrorMessage} />
	),
	[UnsplashFetchError.RATE_LIMITED_ERROR]: (
		<FormattedMessage {...messages.unsplashApiRateLimitedErrorMessage} />
	),
};

export const useUnsplash = (
	isPageTitleComponent?: boolean,
	selectedImageUrl?: string,
	initialUnsplashResults?: UnsplashResult[],
): {
	defaultUnsplashResults: UnsplashResult[] | undefined;
	errorMessage: React.JSX.Element | undefined;
	handleError: (error: Error) => void;
	selectedDefaultImageUrl: string | undefined;
	setDefaultUnsplashResults: React.Dispatch<React.SetStateAction<UnsplashResult[] | undefined>>;
	setError: React.Dispatch<React.SetStateAction<UnsplashFetchError | undefined>>;
	setUnsplashSearchResults: React.Dispatch<React.SetStateAction<UnsplashResult[] | undefined>>;
	unSplashSearchResults: UnsplashResult[] | undefined;
} => {
	const [defaultUnsplashResults, setDefaultUnsplashResults] = useState<
		UnsplashResult[] | undefined
	>([]);
	const [unSplashSearchResults, setUnsplashSearchResults] = useState<UnsplashResult[] | undefined>(
		[],
	);
	const [fetchError, setError] = useState<UnsplashFetchError | undefined>();
	const contentTopperDefaultImageUrl = isUnsplashImage(selectedImageUrl) ? selectedImageUrl : '';
	const selectedDefaultImageUrl = !isPageTitleComponent
		? selectedImageUrl
		: contentTopperDefaultImageUrl;

	const handleError = useCallback(
		(error: Error) => {
			if (isRateLimitedError(error)) {
				setError(UnsplashFetchError.RATE_LIMITED_ERROR);
			} else if (isBadStatusError(error)) {
				setError(UnsplashFetchError.BAD_STATUS_ERROR);
			} else {
				setError(UnsplashFetchError.NO_NETWORK_ERROR);
			}
			setUnsplashSearchResults(undefined);
		},
		[setError, setUnsplashSearchResults],
	);

	const { submitError } = useProductDependencies();

	useEffect(() => {
		if (!initialUnsplashResults) {
			const defaultUnsplashQueryWord = isPageTitleComponent
				? PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD
				: DEFAULT_UNSPLASH_QUERY_WORD;
			void getUnsplashPhotos(defaultUnsplashQueryWord, 1, submitError, isPageTitleComponent)
				.then((result) => {
					setUnsplashSearchResults(result);
					setDefaultUnsplashResults(result);
				})
				.catch((responseError) => {
					handleError(responseError);
				});
		} else {
			setUnsplashSearchResults(initialUnsplashResults);
			setDefaultUnsplashResults(initialUnsplashResults);
		}
	}, [initialUnsplashResults, isPageTitleComponent, handleError, submitError]);

	return {
		selectedDefaultImageUrl,
		defaultUnsplashResults,
		unSplashSearchResults,
		errorMessage: fetchError ? i18nErrorText[fetchError] : undefined,
		handleError,
		setUnsplashSearchResults,
		setDefaultUnsplashResults,
		setError,
	};
};
